import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useTeamMember, useUpdateTeamMember } from "@/hooks/use-api";
import type { UpdateTeamMemberInput } from "@adva/types";
import {
  TEAM_MEMBER_FIELDS,
  teamMemberToFormValues,
  type TeamMemberFormValues,
} from "./team-member-form-config";

export default function V2TeamMemberEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const teamMemberQuery = useTeamMember(id);
  const updateTeamMember = useUpdateTeamMember();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(values: TeamMemberFormValues) {
    setError(null);
    try {
      await updateTeamMember.mutateAsync({
        id,
        input: values as UpdateTeamMemberInput,
      });
      toast.success("Team member updated");
      navigate(`/team-members/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  const teamMember = (
    teamMemberQuery.data as
      | { team_member?: Record<string, unknown> }
      | undefined
  )?.team_member;

  if (teamMemberQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (teamMemberQuery.isError || !teamMember) {
    return (
      <EmptyState
        title="Team member not found"
        description="This team member may have been removed, or you don't have access to them."
        action={{
          label: "Back to team members",
          onClick: () => navigate("/team-members"),
        }}
      />
    );
  }

  return (
    <EntityForm<TeamMemberFormValues>
      title="Edit team member"
      breadcrumbs={[
        { label: "Team members", href: "/team-members" },
        { label: "Edit" },
      ]}
      defaultValues={teamMemberToFormValues(teamMember)}
      fields={TEAM_MEMBER_FIELDS}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref={`/team-members/${id}`}
      error={error}
      isSubmitting={updateTeamMember.isPending}
    />
  );
}
